import { useEffect, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { HugeiconsIcon } from '@hugeicons/react'
import {
  Building03Icon,
  UnfoldMoreIcon,
} from '@hugeicons/core-free-icons'

import { authClient, useSession } from '@/lib/auth-client'
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from '@/components/ui/sidebar'
import { Skeleton } from '@/components/ui/skeleton'

interface Org {
  id: string
  name: string
}

export function BranchSwitcher() {
  const location = useLocation()
  const session = useSession()
  const [orgs, setOrgs] = useState<Org[]>([])
  const [loading, setLoading] = useState(true)

  // The active branch lives on the session — it updates automatically after
  // setActive (Better Auth fires the session signal), so deriving from here
  // keeps the label in sync without a second reactive query.
  const activeOrgId =
    (session.data?.session as { activeOrganizationId?: string } | undefined)
      ?.activeOrganizationId ?? null

  // Load the branches the user belongs to once the session is ready.
  useEffect(() => {
    if (!session.data) return
    let cancelled = false
    authClient.organization
      .list()
      .then((res) => {
        if (cancelled) return
        const list = ((res.data as Org[] | undefined) ?? []).map((o) => ({
          id: o.id,
          name: o.name,
        }))
        setOrgs(list)
        setLoading(false)
      })
      .catch(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [session.data])

  if (loading && orgs.length === 0) {
    return (
      <SidebarMenu>
        <SidebarMenuItem>
          <div className="flex items-center gap-2 p-2">
            <Skeleton className="size-8 rounded-lg" />
            <Skeleton className="h-4 w-24" />
          </div>
        </SidebarMenuItem>
      </SidebarMenu>
    )
  }

  const active = orgs.find((o) => o.id === activeOrgId) ?? null
  const label = active?.name ?? 'Seleccionar sucursal'
  const from = `${location.pathname}${location.search}${location.hash}`

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <SidebarMenuButton
          asChild
          size="lg"
          className="rounded-xl border border-sidebar-border"
        >
          <Link to="/select-branch" state={{ from }} aria-label={`Cambiar sucursal. Actual: ${label}`}>
            <div className="flex aspect-square size-8 items-center justify-center rounded-lg bg-sidebar-accent text-sidebar-accent-foreground">
              <HugeiconsIcon icon={Building03Icon} strokeWidth={2} className="size-4" />
            </div>
            <div className="flex min-w-0 flex-1 flex-col gap-1 leading-none">
              <span className="text-xs text-muted-foreground">Sucursal actual</span>
              <span className="truncate font-medium">{label}</span>
            </div>
            <HugeiconsIcon icon={UnfoldMoreIcon} className="ml-auto size-4" />
          </Link>
        </SidebarMenuButton>
      </SidebarMenuItem>
    </SidebarMenu>
  )
}
